import { Check, Edit, MoreVert } from '@mui/icons-material';
import { Box, IconButton, Stack, TextField, Typography, useMediaQuery, useTheme } from '@mui/material';
import React, { useState } from 'react';
import ConfirmationDialog from '../../../../ConfirmationDialog/ConfirmationDialog';
import TodoListTitlePopupMenu from '../../PopupMenu/TodoListTitlePopupMenu';

interface ListHeaderProps {
    title: string;
    onTitleChange: (newTitle: string) => void;
    onDelete: () => void;
}

const ListHeader: React.FC<ListHeaderProps> = ({
    title,
    onTitleChange,
    onDelete,
}) => {
    const theme = useTheme();
    const [isEditing, setIsEditing] = useState(false);
    const [editedTitle, setEditedTitle] = useState(title);
    const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
    const [isHovered, setIsHovered] = useState(false);
    const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

    const showHover = isMobile || isHovered;

    const handleEditClick = () => {
        if (isEditing)
            handleTitleSubmit();
        else
            setIsEditing(true);
    };

    const handleTitleSubmit = () => {
        if (editedTitle.trim())
            onTitleChange(editedTitle);
        setIsEditing(false);
    };

    const handleKeyPress = (e: React.KeyboardEvent) => {
        if (e.key === 'Enter') {
            handleTitleSubmit();
        } else if (e.key === 'Escape') {
            setIsEditing(false);
            setEditedTitle(title);
        }
    };

    const handleDeleteClick = () => {
        setAnchorEl(null); // Close the menu
        setIsDeleteDialogOpen(true); // Open the confirmation dialog
    };

    return (
        <Stack
            direction="row"
            alignItems="center"
            spacing={1}
            onMouseEnter={() => setIsHovered(true)}
            onMouseLeave={() => setIsHovered(false)}
            onContextMenu={(e) => {
                e.preventDefault();
                setAnchorEl(e.currentTarget)
            }}
            onDoubleClick={handleEditClick}
            sx={{ position: 'relative', minHeight: 40 }}
        >
            {isEditing ? (
                <TextField
                    autoFocus
                    value={editedTitle}
                    onChange={(e) => setEditedTitle(e.target.value)}
                    onBlur={handleTitleSubmit}
                    onKeyDown={handleKeyPress}
                    size="small"
                    fullWidth
                    sx={{ mr: 'auto' }}
                />
            ) : (
                <Typography variant="h6" sx={{ flex: 1 }}>
                    {title}
                </Typography>
            )}

            <Box
                sx={{
                    opacity: showHover ? 1 : 0,
                    transition: 'opacity 0.2s',
                    display: 'flex',
                    gap: 0.5
                }}
            >
                <IconButton
                    size="small"
                    onClick={handleEditClick}
                    sx={{ color: isEditing ? 'success.main' : 'info.main' }}
                >
                    {isEditing ? <Check fontSize="small" /> : <Edit fontSize="small" />}
                </IconButton>
                <IconButton
                    size="small"
                    onClick={(e) => setAnchorEl(e.currentTarget)}
                    sx={{ color: 'text.secondary' }}
                >
                    <MoreVert fontSize="small" />
                </IconButton>
            </Box>

            <TodoListTitlePopupMenu
                anchorEl={anchorEl}
                onClose={() => setAnchorEl(null)}
                onDelete={handleDeleteClick}
            />

            <ConfirmationDialog
                open={isDeleteDialogOpen}
                title="Delete List"
                message={`Are you sure you want to delete "${title}"? This action cannot be undone.`}
                confirmText="Delete"
                severity="error"
                onConfirm={onDelete}
                onCancel={() => setIsDeleteDialogOpen(false)}
            />
        </Stack>
    );
};

export default ListHeader; 